import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Shield, Server, FileText } from 'lucide-react';
import { formatPriority } from './fusionFormat';

const sourceLabels = { custom: 'Atelier 5', dreadedEvent: 'Atelier 1', ecosystem: 'Atelier 3' };

// Category buckets mirroring the Atelier 5 budget table colors.
export const CATEGORY_META = [
  { key: 'governance', match: ['governance', 'gouvernance et anticipation'], cls: 'bg-purple-100 text-purple-700', labelKey: 'fusionPtr.catGovernance', fallback: 'Gouvernance' },
  { key: 'protection', match: ['protection'], cls: 'bg-blue-100 text-blue-700', labelKey: 'fusionPtr.catProtection', fallback: 'Protection' },
  { key: 'defense', match: ['defense', 'défense'], cls: 'bg-green-100 text-green-700', labelKey: 'fusionPtr.catDefense', fallback: 'Défense' },
  { key: 'resilience', match: ['resilience', 'résilience'], cls: 'bg-orange-100 text-orange-700', labelKey: 'fusionPtr.catResilience', fallback: 'Résilience' },
];

// Per-analysis summary powering the card header: counts, total budget, category split.
export const summarizeSnapshot = (snap) => {
  const measures = snap?.measures || [];
  const budgetTotal = measures.reduce((sum, m) => sum + (Number(m.budget) || 0), 0);
  const cats = { governance: 0, protection: 0, defense: 0, resilience: 0, other: 0 };
  measures.forEach((m) => {
    const cat = String(m.category || '').toLowerCase();
    const bucket = CATEGORY_META.find((c) => c.match.includes(cat));
    cats[bucket ? bucket.key : 'other'] += 1;
  });
  return {
    measuresCount: measures.length,
    assetsCount: (snap?.supportAssets || []).length,
    budgetTotal,
    cats,
  };
};

// One analysis snapshot: header summary + perimetre, biens supports, measures.
// Own expanded state — shared by the compare grid and the details modal.
const SnapshotCard = ({ snap }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState({});

  const key = String(snap.analysisId);
  const scope = snap.perimetre?.scope || '';
  const truncated = scope.length > 300 ? `${scope.slice(0, 300)}…` : scope;
  const summary = summarizeSnapshot(snap);
  const toggle = (section) => setExpanded((prev) => ({ ...prev, [section]: !prev[section] }));

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center gap-2">
          <FileText size={15} className="text-blue-500 flex-shrink-0" />
          <span className="text-sm font-semibold text-slate-800 truncate">{snap.analysisName}</span>
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500 tabular-nums">
          <span>{t('fusionPtr.measuresCount', '{{count}} mesures', { count: summary.measuresCount })}</span>
          <span>{t('fusionPtr.assetsCount', '{{count}} actifs', { count: summary.assetsCount })}</span>
          <span>{t('fusionPtr.budgetTotal', 'Budget total : {{amount}} €', { amount: summary.budgetTotal.toLocaleString() })}</span>
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {CATEGORY_META.filter((c) => summary.cats[c.key] > 0).map((c) => (
            <span key={c.key} className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium tabular-nums ${c.cls}`}>
              {t(c.labelKey, c.fallback)} · {summary.cats[c.key]}
            </span>
          ))}
        </div>
      </div>
      <div className="p-4 space-y-4 text-xs">
        <div>
          <div className="font-semibold text-slate-600 mb-1">{t('fusionPtr.perimetre', 'Périmètre')}</div>
          {scope ? (
            <p className="text-slate-600 whitespace-pre-wrap">{expanded.scope ? scope : truncated}</p>
          ) : (
            <p className="text-slate-400 italic">{t('fusionPtr.noPerimetre', 'Périmètre non renseigné')}</p>
          )}
          {scope.length > 300 && (
            <button type="button" onClick={() => toggle('scope')} className="text-blue-600 hover:text-blue-800 mt-1">
              {expanded.scope ? t('fusionPtr.showLess', 'Réduire') : t('fusionPtr.showMore', 'Voir plus')}
            </button>
          )}
          {(snap.perimetre?.missions?.length > 0) && (
            <ul className="mt-1.5 list-disc list-inside text-slate-600 space-y-0.5">
              {snap.perimetre.missions.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          )}
        </div>
        <div>
          <button type="button" onClick={() => toggle('assets')} className="flex items-center gap-1 font-semibold text-slate-600 hover:text-slate-800">
            {expanded.assets ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            <Server size={13} />
            {t('fusionPtr.supportAssets', 'Biens supports ({{count}})', { count: snap.supportAssets?.length || 0 })}
          </button>
          {expanded.assets && (
            <ul className="mt-1.5 space-y-1">
              {(snap.supportAssets || []).map((sa, i) => (
                <li key={sa.id || i} className="text-slate-600">
                  <span className="font-medium text-slate-700">{sa.name}</span>
                  {sa.type && <span className="text-slate-400"> — {sa.type}</span>}
                  {sa.businessValueName && <span className="text-slate-400"> ({sa.businessValueName})</span>}
                </li>
              ))}
              {!(snap.supportAssets?.length) && <li className="text-slate-400 italic">{t('fusionPtr.noAssets', 'Aucun bien support')}</li>}
            </ul>
          )}
        </div>
        <div>
          <button type="button" onClick={() => toggle('measures')} className="flex items-center gap-1 font-semibold text-slate-600 hover:text-slate-800">
            {expanded.measures ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            <Shield size={13} />
            {t('fusionPtr.measures', 'Mesures identifiées ({{count}})', { count: snap.measures?.length || 0 })}
          </button>
          {expanded.measures && (
            <div className="mt-1.5 overflow-x-auto">
              <table className="w-full text-[11px] border border-slate-200">
                <thead>
                  <tr className="bg-slate-50 text-slate-600">
                    <th className="text-left px-2 py-1 font-semibold">{t('fusionPtr.measure', 'Mesure')}</th>
                    <th className="text-left px-2 py-1 font-semibold">{t('fusionPtr.source', 'Source')}</th>
                    <th className="text-left px-2 py-1 font-semibold">{t('fusionPtr.priority', 'Priorité')}</th>
                    <th className="text-right px-2 py-1 font-semibold">{t('fusionPtr.budget', 'Budget')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(snap.measures || []).map((m) => (
                    <tr key={m.key} className="hover:bg-slate-50">
                      <td className="px-2 py-1 text-slate-700" title={m.description || m.name}>
                        {m.name || '—'}
                        {m.attackRefs && <span className="text-slate-400 ml-1">[{m.attackRefs}]</span>}
                      </td>
                      <td className="px-2 py-1 text-slate-500">{sourceLabels[m.source] || m.source}</td>
                      <td className="px-2 py-1 text-slate-500">{formatPriority(m.priority) || '—'}</td>
                      <td className="px-2 py-1 text-right text-slate-500">{m.budget ? `${m.budget} €` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

SnapshotCard.propTypes = {
  snap: PropTypes.shape({
    analysisId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    analysisName: PropTypes.string,
    perimetre: PropTypes.object,
    supportAssets: PropTypes.array,
    measures: PropTypes.array,
  }).isRequired,
};

export default SnapshotCard;
